import { createContext, useContext, useReducer, useEffect, useMemo } from 'react';
import { authService } from '../services/api';
import toast from 'react-hot-toast';

const AuthContext = createContext();

const AUTH_STATES = {
  IDLE: 'idle',
  LOADING: 'loading',
  AUTHENTICATED: 'authenticated',
  UNAUTHENTICATED: 'unauthenticated',
  CODE_SENT: 'code_sent'
};

const AUTH_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  CODE_SENT: 'CODE_SENT',
  AUTHENTICATE_SUCCESS: 'AUTHENTICATE_SUCCESS',
  LOGOUT: 'LOGOUT',
  SET_ERROR: 'SET_ERROR'
};

const initialState = {
  status: AUTH_STATES.IDLE,
  user: null,
  token: null,
  maskedEmail: null,
  currentNit: null,
  loading: false,
  error: null
};

const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
        error: null
      };

    case AUTH_ACTIONS.CODE_SENT:
      return {
        ...state,
        status: AUTH_STATES.CODE_SENT,
        maskedEmail: action.payload.maskedEmail,
        currentNit: action.payload.nit,
        loading: false,
        error: null
      };

    case AUTH_ACTIONS.AUTHENTICATE_SUCCESS:
      localStorage.setItem('auth_token', action.payload.token);
      localStorage.setItem('user_data', JSON.stringify(action.payload.user));

      return {
        ...state,
        status: AUTH_STATES.AUTHENTICATED,
        user: action.payload.user,
        token: action.payload.token,
        loading: false,
        error: null,
        maskedEmail: null,
        currentNit: null
      };

    case AUTH_ACTIONS.LOGOUT:
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_data');

      return {
        ...initialState,
        status: AUTH_STATES.UNAUTHENTICATED
      };

    case AUTH_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false
      };

    default:
      return state;
  }
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    let mounted = true;

    (async () => {
      const token = localStorage.getItem('auth_token');

      if (!token) {
        if (mounted) dispatch({ type: AUTH_ACTIONS.LOGOUT });
        return;
      }

      try {
        const { data } = await authService.getMe();

        if (!mounted) return;

        if (data?.success && data?.user) {
          localStorage.setItem('user_data', JSON.stringify(data.user));
          dispatch({
            type: AUTH_ACTIONS.AUTHENTICATE_SUCCESS,
            payload: { token, user: data.user },
          });
        } else {
          // respuesta 200 pero success:false -> forzar logout
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user_data');
          dispatch({ type: AUTH_ACTIONS.LOGOUT });
        }
      } catch {
        // error de red / 401 / etc -> forzar logout
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user_data');
        if (mounted) dispatch({ type: AUTH_ACTIONS.LOGOUT });
      }
    })();

    return () => { mounted = false; };
  }, []);


  const login = async (nit) => {
    dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: null });
    dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });

    try {
      const res = await authService.login(nit);

      if (res.data.success) {
        dispatch({
          type: AUTH_ACTIONS.CODE_SENT,
          payload: { maskedEmail: res.data.maskedEmail, nit }
        });
        toast.success('Código de verificación enviado a su email');
        return { success: true };
      }

      const msg = res.data.message || 'No se pudo iniciar sesión';
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: msg });
      toast.error(msg);
      return { success: false, message: msg };
    } catch (e) {
      const msg = e.response?.data?.message || 'Error iniciando sesión';
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: msg });
      toast.error(msg);
      return { success: false, message: msg };
    } finally {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
    }
  };

  // Verificar código
  const verifyCode = async (code) => {
    if (!state.currentNit) {
      const msg = 'Error: NIT no encontrado. Por favor, inicie el proceso nuevamente.';
      toast.error(msg);
      return { success: false, message: msg };
    }

    dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: null });
    dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });

    try {
      const res = await authService.verify(state.currentNit, code);

      if (res.data.success) {
        localStorage.setItem('auth_token', res.data.token);
        localStorage.setItem('user_data', JSON.stringify(res.data.user));

        dispatch({
          type: AUTH_ACTIONS.AUTHENTICATE_SUCCESS,
          payload: { token: res.data.token, user: res.data.user }
        });
        toast.success('¡Bienvenido al sistema de pagos de EC Group Logistics!');
        return { success: true };
      }

      const msg = res.data.message || 'Código inválido';
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: msg });
      toast.error(msg);
      return { success: false, message: msg };
    } catch (e) {
      const msg = e.response?.data?.message || 'Error verificando código';
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: msg });
      toast.error(msg);
      return { success: false, message: msg };
    } finally {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
    }
  };

  // Logout
  const logout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_data');
    dispatch({ type: AUTH_ACTIONS.LOGOUT });
    toast.success('Sesión cerrada correctamente');
  };

  // Renovar token
  const refreshToken = async () => {
    try {
      const res = await authService.refresh();
      if (res.data.success) {
        localStorage.setItem('auth_token', res.data.token);
        return { success: true };
      }
      logout();
      return { success: false };
    } catch {
      logout();
      return { success: false };
    }
  };

  const registrarUsuario = async (nit, correo) => {
    dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: null });
    dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });

    try {
      const res = await authService.register(nit, correo);

      if (res.data.success) {
        toast.success('Usuario registrado exitosamente');
        return { success: true };
      }

      const msg = res.data.message || 'No se pudo registrar el usuario';
      toast.error(msg);
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: msg });
      return { success: false, message: msg };
    } catch (e) {
      const msg = e.response?.data?.message || 'Error registrando usuario';
      toast.error(msg);
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: msg });
      return { success: false, message: msg };
    } finally {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
    }
  };

  const value = useMemo(() => ({
    ...state,
    isAuthenticated: state.status === AUTH_STATES.AUTHENTICATED,
    isCodeSent: state.status === AUTH_STATES.CODE_SENT,

    login,
    verifyCode,
    logout,
    refreshToken,
    registrarUsuario
  }), [state, login, verifyCode, logout, refreshToken, registrarUsuario]);


  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

export { AUTH_STATES };